import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Scissors, Drop, Wrench, Plus, Trash, CalendarBlank, Leaf, Flag, Bug, ShieldWarning, Sparkle, ChatCircleDots } from '@phosphor-icons/react';
import axios from 'axios';
import { toast } from 'sonner';
import { API_URL } from '../config/api';

const ACTIVITY_TYPES = [
  { value: 'mowing', label: 'Mowing', icon: Scissors, color: 'text-green-600', bg: 'bg-green-100 dark:bg-green-900/30' },
  { value: 'watering', label: 'Manual Watering', icon: Drop, color: 'text-blue-600', bg: 'bg-blue-100 dark:bg-blue-900/30' },
  { value: 'maintenance', label: 'Maintenance', icon: Wrench, color: 'text-amber-600', bg: 'bg-amber-100 dark:bg-amber-900/30' },
  { value: 'fertilize', label: 'Fertilize', icon: Leaf, color: 'text-emerald-600', bg: 'bg-emerald-100 dark:bg-emerald-900/30' },
  { value: 'weed_control', label: 'Weed Control', icon: Flag, color: 'text-lime-600', bg: 'bg-lime-100 dark:bg-lime-900/30' },
  { value: 'pest_control', label: 'Pest Control', icon: Bug, color: 'text-red-600', bg: 'bg-red-100 dark:bg-red-900/30' },
  { value: 'disease_control', label: 'Disease Control', icon: ShieldWarning, color: 'text-purple-600', bg: 'bg-purple-100 dark:bg-purple-900/30' },
];

const MAINTENANCE_TYPES = [
  'Aeration', 'Dethatching', 'Overseeding', 'Soil Test', 'Blade Sharpening',
  'Leveling', 'Edging', 'Leaf Cleanup', 'Winterizing', 'Other',
];

export const ActivityLog = () => {
  const [activities, setActivities] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    type: 'mowing', date: new Date().toISOString().split('T')[0],
    mow_height: '', duration_minutes: '', water_amount_inches: '',
    maintenance_type: '', zone_name: '', notes: ''
  });
  // Moe "ahead of schedule" prompt state
  const [moePrompt, setMoePrompt] = useState(null); // { activityId, matches: [{idx, action, detail}] }
  const [moeLinking, setMoeLinking] = useState(false);

  useEffect(() => {
    fetchActivities();
  }, []);

  const fetchActivities = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/activities?limit=15`, { withCredentials: true });
      setActivities(res.data);
    } catch {}
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await axios.post(`${API_URL}/api/activities`, form, { withCredentials: true });
      toast.success('Activity logged');
      setShowAdd(false);
      const saved = res.data;
      setForm({ type: 'mowing', date: new Date().toISOString().split('T')[0], mow_height: '', duration_minutes: '', water_amount_inches: '', maintenance_type: '', zone_name: '', notes: '' });
      fetchActivities();
      // If Moe found a pending Smart Plan action that matches, prompt the user
      if (saved?.matching_plan_actions && saved.matching_plan_actions.length > 0) {
        setMoePrompt({ activityId: saved.id, matches: saved.matching_plan_actions });
      }
    } catch { toast.error('Failed to log activity'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API_URL}/api/activities/${id}`, { withCredentials: true });
      setActivities(prev => prev.filter(a => a.id !== id));
    } catch { toast.error('Failed to delete'); }
  };

  const handleMoeMarkDone = async (match) => {
    if (!moePrompt) return;
    setMoeLinking(true);
    try {
      await axios.put(
        `${API_URL}/api/smart-plan/complete-action`,
        { action_idx: match.idx, completed: true, link_activity_id: moePrompt.activityId },
        { withCredentials: true }
      );
      toast.success(`Nice — "${match.action}" marked done on your Smart Plan`);
      setMoePrompt(null);
      fetchActivities();
    } catch {
      toast.error('Could not link to Smart Plan');
    } finally {
      setMoeLinking(false);
    }
  };

  const getTypeConfig = (type) => ACTIVITY_TYPES.find(t => t.value === type) || ACTIVITY_TYPES[0];

  return (
    <Card data-testid="activity-log-card">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-base sm:text-lg flex items-center gap-2">
              <CalendarBlank size={20} className="text-indigo-600 dark:text-indigo-400" />
              Activity Log
            </CardTitle>
            <p className="text-xs text-muted-foreground mt-1">
              Unplanned work? Log it here — Moe will sync it with your Smart Plan.
            </p>
          </div>
          <Button data-testid="log-activity-btn" size="sm" onClick={() => setShowAdd(true)}>
            <Plus size={14} weight="bold" className="mr-1" /> Log
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {activities.length > 0 ? (
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {activities.map(act => {
              const cfg = getTypeConfig(act.type);
              const Icon = cfg.icon;
              const fromPlan = act.source === 'smart_plan';
              return (
                <div key={act.id} className="flex items-center gap-3 py-2 border-b border-border/50 last:border-0" data-testid={`activity-${act.id}`}>
                  <div className={`w-8 h-8 rounded-lg ${cfg.bg} flex items-center justify-center shrink-0`}>
                    <Icon size={16} className={cfg.color} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium">{cfg.label}</span>
                      {act.zone_name && <span className="text-xs text-muted-foreground">({act.zone_name})</span>}
                      {fromPlan && (
                        <span
                          data-testid={`smart-plan-badge-${act.id}`}
                          className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300"
                          title="Mirrored from your Smart Plan"
                        >
                          <Sparkle size={10} weight="fill" /> from Smart Plan
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <span>{new Date(act.date + 'T12:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                      {act.details?.mow_height && <span>Height: {act.details.mow_height}"</span>}
                      {act.details?.duration_minutes && <span>{act.details.duration_minutes} min</span>}
                      {act.details?.water_amount_inches && <span>{act.details.water_amount_inches}"</span>}
                      {act.details?.maintenance_type && <span>{act.details.maintenance_type}</span>}
                      {act.notes && <span className="truncate">{act.notes}</span>}
                    </div>
                  </div>
                  {!fromPlan && (
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-muted-foreground hover:text-red-500" onClick={() => handleDelete(act.id)}>
                      <Trash size={14} />
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground text-center py-4">No activities logged yet</p>
        )}
      </CardContent>

      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Log Activity</DialogTitle>
            <DialogDescription>Record what you did for your lawn today</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Activity Type</Label>
                <Select value={form.type} onValueChange={v => setForm(p => ({ ...p, type: v }))}>
                  <SelectTrigger data-testid="activity-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ACTIVITY_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Date</Label>
                <Input data-testid="activity-date" type="date" value={form.date} onChange={e => setForm(p => ({ ...p, date: e.target.value }))} />
              </div>
            </div>

            {form.type === 'mowing' && (
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Mowing Height (inches)</Label>
                  <Input data-testid="mow-height" type="number" step="0.25" min="0.5" max="6" placeholder="e.g., 3.0" value={form.mow_height} onChange={e => setForm(p => ({ ...p, mow_height: e.target.value }))} />
                </div>
                <div className="space-y-2">
                  <Label>Duration (minutes)</Label>
                  <Input data-testid="mow-duration" type="number" min="1" placeholder="e.g., 45" value={form.duration_minutes} onChange={e => setForm(p => ({ ...p, duration_minutes: e.target.value }))} />
                </div>
              </div>
            )}

            {form.type === 'watering' && (
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Amount (inches)</Label>
                  <Input data-testid="water-amount" type="number" step="0.1" min="0.1" placeholder="e.g., 0.5" value={form.water_amount_inches} onChange={e => setForm(p => ({ ...p, water_amount_inches: e.target.value }))} />
                </div>
                <div className="space-y-2">
                  <Label>Duration (minutes)</Label>
                  <Input type="number" min="1" placeholder="e.g., 30" value={form.duration_minutes} onChange={e => setForm(p => ({ ...p, duration_minutes: e.target.value }))} />
                </div>
              </div>
            )}

            {form.type === 'maintenance' && (
              <div className="space-y-2">
                <Label>Maintenance Type</Label>
                <Select value={form.maintenance_type} onValueChange={v => setForm(p => ({ ...p, maintenance_type: v }))}>
                  <SelectTrigger data-testid="maintenance-type-select"><SelectValue placeholder="Select type" /></SelectTrigger>
                  <SelectContent>
                    {MAINTENANCE_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Zone (optional)</Label>
                <Input data-testid="activity-zone" placeholder="e.g., Front Yard" value={form.zone_name} onChange={e => setForm(p => ({ ...p, zone_name: e.target.value }))} />
              </div>
              <div className="space-y-2">
                <Label>Notes (optional)</Label>
                <Input data-testid="activity-notes" placeholder="e.g., Bagged clippings" value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} />
              </div>
            </div>

            <Button data-testid="save-activity-btn" onClick={handleSave} disabled={saving} className="w-full">
              {saving ? 'Saving...' : 'Log Activity'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Moe "ahead of schedule" prompt — shown only when the logged activity matches a pending Smart Plan action */}
      <Dialog open={!!moePrompt} onOpenChange={(o) => !o && setMoePrompt(null)}>
        <DialogContent data-testid="moe-ahead-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ChatCircleDots size={22} weight="fill" className="text-emerald-600" />
              Oh, you're ahead of schedule!
            </DialogTitle>
            <DialogDescription>
              Moe noticed this overlaps with your Smart Plan. Want to mark it done?
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 my-2">
            {moePrompt?.matches?.map((m) => (
              <div key={m.idx} className="flex items-start justify-between gap-3 p-3 rounded-lg border border-emerald-200 dark:border-emerald-900/50 bg-emerald-50 dark:bg-emerald-900/20">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-emerald-900 dark:text-emerald-100">{m.action}</div>
                  {m.detail && <div className="text-xs text-emerald-800/80 dark:text-emerald-200/70 mt-1">{m.detail}</div>}
                </div>
                <Button
                  data-testid={`moe-mark-done-${m.idx}`}
                  size="sm"
                  disabled={moeLinking}
                  onClick={() => handleMoeMarkDone(m)}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white shrink-0"
                >
                  Mark done
                </Button>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button data-testid="moe-dismiss-btn" variant="ghost" onClick={() => setMoePrompt(null)}>
              Not this one
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};
